import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { useSnackbar } from 'notistack';
import { v4 as uuid } from 'uuid';

import { Box, Typography } from '@mui/material';

import { MAX_SVC_IMAGE_UPLOAD_BYTES } from '~/services/ImageService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import FlatImageTile from './FlatImageTile';
import ImageUploadTile from './ImageUploadTile';
import PendingImageTile from './PendingImageTile';
import {
  createCroppedImageFile,
  getCenteredCropArea,
  getCropAreaForAspectRatio,
  getDefaultPendingCropAspectRatio,
  getImageDimensions,
  normalizeImageFileForUpload,
} from './cropUtils';
import { DEFAULT_PENDING_IMAGE_ADJUSTMENTS, PendingImageAdjustments, PendingImageItem } from './pendingUploadTypes';
import {
  FlatImageEditorHandle,
  FlatImageEditorProps,
  FlatImageEditorUploadAllPendingResult,
  FlatImageEditorUploadFailure,
  FlatImageExtraFieldValue,
  FlatImageItem,
} from './types';

const DEFAULT_PENDING_UPLOAD_ASPECT_RATIO = 16 / 9;

function reindexItems(items: Array<FlatImageItem>) {
  return items.map((item, index) => ({
    ...item,
    order: index + 1,
  }));
}

function idsMatch(leftId: string | number, rightId: string | number) {
  return String(leftId) === String(rightId);
}

function filterPendingItems(items: Array<FlatImageItem>, pendingIds: Array<string | number>) {
  return items.filter((item) => !pendingIds.some((pendingId) => idsMatch(item.id, pendingId)));
}

function revokePendingItemUrls(items: Array<PendingImageItem>) {
  items.forEach((item) => {
    URL.revokeObjectURL(item.objectUrl);
  });
}

type PendingUploadAttemptResult =
  | {
      localId: string;
      status: 'fulfilled';
    }
  | ({
      localId: string;
      status: 'rejected';
    } & FlatImageEditorUploadFailure);

const FlatImageEditor = forwardRef<FlatImageEditorHandle, FlatImageEditorProps>(function FlatImageEditor(
  props: FlatImageEditorProps,
  ref: React.ForwardedRef<FlatImageEditorHandle>,
) {
  const {
    items,
    onDelete,
    onItemsChange,
    onPendingUploadStateChange,
    onSetHero,
    onToggleMarkedForDeletion,
    onUploadFile,
    enablePendingCropUpload = false,
    pendingUploadAspectRatio = DEFAULT_PENDING_UPLOAD_ASPECT_RATIO,
    uploadLabel,
    uploadHelperText,
    uploadDisabled = false,
    showDescriptionField = true,
    showDeleteAction = true,
    showHeroAction = false,
    showHiddenToggle = false,
    deleteActionMode = 'delete',
    deleteActionLabel = 'Delete image',
    restoreActionLabel = 'Restore image',
    deleteUndoMs = 2000,
    descriptionLabel = 'Short description',
    emptyStateMessage = 'No images added yet',
    extraFields = [],
    disabled = false,
    renderItemActions,
    showReorderAction = true,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [isPreparingUploads, setIsPreparingUploads] = useState(false);
  const [localItems, setLocalItems] = useState<Array<FlatImageItem>>(items);
  const [pendingItems, setPendingItems] = useState<Array<PendingImageItem>>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Array<string | number>>([]);
  const localItemsRef = useRef<Array<FlatImageItem>>(items);
  const pendingItemsRef = useRef<Array<PendingImageItem>>([]);
  const pendingDeleteTimersRef = useRef<Record<string, number>>({});
  const pendingDeleteIdsRef = useRef<Array<string | number>>([]);
  const pendingDeleteSnapshotsRef = useRef<Record<string, { item: FlatImageItem; index: number }>>({});

  useEffect(() => {
    localItemsRef.current = localItems;
  }, [localItems]);

  useEffect(() => {
    pendingItemsRef.current = pendingItems;
  }, [pendingItems]);

  useEffect(() => {
    onPendingUploadStateChange?.({
      isUploadingPending: isPreparingUploads || pendingItems.some((item) => item.status === 'uploading'),
      pendingUploadCount: pendingItems.length,
    });
  }, [isPreparingUploads, onPendingUploadStateChange, pendingItems]);

  useEffect(() => {
    const pendingDeleteSnapshots = Object.values(pendingDeleteSnapshotsRef.current);

    if (!pendingDeleteSnapshots.length) {
      localItemsRef.current = items;
      setLocalItems(items);
      return;
    }

    const mergedItems = [...items];

    pendingDeleteSnapshots
      .sort((left, right) => left.index - right.index)
      .forEach(({ item, index }) => {
        if (mergedItems.some((existingItem) => idsMatch(existingItem.id, item.id))) {
          return;
        }

        mergedItems.splice(Math.min(index, mergedItems.length), 0, item);
      });

    const nextItems = reindexItems(mergedItems);
    localItemsRef.current = nextItems;
    setLocalItems(nextItems);
  }, [items]);

  useEffect(
    () => () => {
      Object.values(pendingDeleteTimersRef.current).forEach((timer) => window.clearTimeout(timer));
      pendingDeleteSnapshotsRef.current = {};
      revokePendingItemUrls(pendingItemsRef.current);
    },
    [],
  );

  useEffect(() => {
    pendingDeleteIdsRef.current = pendingDeleteIds;
  }, [pendingDeleteIds]);

  const updatePendingItems = useCallback(
    (updater: Array<PendingImageItem> | ((items: Array<PendingImageItem>) => Array<PendingImageItem>)) => {
      setPendingItems((previousItems) => {
        const nextItems = typeof updater === 'function' ? updater(previousItems) : updater;
        const removedItems = previousItems.filter(
          (previousItem) => !nextItems.some((nextItem) => nextItem.localId === previousItem.localId),
        );

        revokePendingItemUrls(removedItems);

        return nextItems;
      });
    },
    [],
  );
  const updatePendingItemById = useCallback(
    (localId: string, updater: (item: PendingImageItem) => PendingImageItem) => {
      updatePendingItems((previousItems) =>
        previousItems.map((item) => {
          if (item.localId !== localId) {
            return item;
          }

          return updater(item);
        }),
      );
    },
    [updatePendingItems],
  );

  const publishItems = useCallback(
    (
      nextItems: Array<FlatImageItem>,
      pendingIds = pendingDeleteIdsRef.current,
      meta?: Parameters<NonNullable<FlatImageEditorProps['onItemsChange']>>[1],
    ) => {
      const publishedItems = reindexItems(filterPendingItems(nextItems, pendingIds));

      onItemsChange?.(publishedItems, meta);
    },
    [onItemsChange],
  );

  const replaceLocalItems = useCallback((nextItems: Array<FlatImageItem>) => {
    localItemsRef.current = nextItems;
    setLocalItems(nextItems);
  }, []);

  const appendUploadedItem = useCallback(
    (newItem: FlatImageItem) => {
      const previousItems = localItemsRef.current;

      if (previousItems.some((item) => idsMatch(item.id, newItem.id))) {
        return;
      }

      const nextItems = reindexItems([...previousItems, newItem]);
      replaceLocalItems(nextItems);
      publishItems(nextItems, pendingDeleteIdsRef.current, { reason: 'upload' });
    },
    [publishItems, replaceLocalItems],
  );

  const uploadPendingItem = useCallback(
    async (pendingItem: PendingImageItem, desiredOrder?: number): Promise<PendingUploadAttemptResult> => {
      if (!onUploadFile) {
        return {
          localId: pendingItem.localId,
          status: 'rejected',
          filename: pendingItem.filename,
          errorMessage: 'Image uploads are not enabled',
        };
      }

      updatePendingItemById(pendingItem.localId, (item) => ({
        ...item,
        status: 'uploading',
        errorMessage: undefined,
      }));

      try {
        const croppedFile = await createCroppedImageFile({
          adjustments: pendingItem.adjustments,
          croppedAreaPixels: pendingItem.croppedAreaPixels,
          file: pendingItem.file,
          outputFileName: pendingItem.filename,
        });
        const normalizedCroppedFile = await normalizeImageFileForUpload({
          file: croppedFile,
          maxBytes: MAX_SVC_IMAGE_UPLOAD_BYTES,
        });
        const newItem = await onUploadFile({
          description: pendingItem.description,
          desiredOrder,
          file: normalizedCroppedFile,
          filename: normalizedCroppedFile.name,
        });

        if (!newItem) {
          throw new Error('Upload callback did not return an image item');
        }

        appendUploadedItem(newItem);
        updatePendingItems((previousItems) => previousItems.filter((item) => item.localId !== pendingItem.localId));
        return {
          localId: pendingItem.localId,
          status: 'fulfilled',
        };
      } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to upload image');

        updatePendingItemById(pendingItem.localId, (item) => ({
          ...item,
          status: 'failed',
          errorMessage,
        }));
        enqueueSnackbar(errorMessage, { variant: 'error' });
        reportError(error);
        return {
          localId: pendingItem.localId,
          status: 'rejected',
          filename: pendingItem.filename,
          errorMessage,
        };
      }
    },
    [appendUploadedItem, enqueueSnackbar, onUploadFile, updatePendingItemById, updatePendingItems],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const commitItems = (
    nextItems: Array<FlatImageItem>,
    meta?: Parameters<NonNullable<FlatImageEditorProps['onItemsChange']>>[1],
  ) => {
    replaceLocalItems(nextItems);
    publishItems(nextItems, pendingDeleteIdsRef.current, meta);
  };

  const handleDescriptionChange = (itemId: string | number, value: string) => {
    if (disabled) {
      return;
    }

    if (pendingDeleteIds.some((pendingId) => idsMatch(pendingId, itemId))) {
      return;
    }

    commitItems(
      localItems.map((item) => {
        if (!idsMatch(item.id, itemId)) {
          return item;
        }

        return {
          ...item,
          description: value,
        };
      }),
      { reason: 'description' },
    );
  };

  const handleExtraFieldChange = (itemId: string | number, fieldKey: string, value: FlatImageExtraFieldValue) => {
    if (disabled) {
      return;
    }

    if (pendingDeleteIds.some((pendingId) => idsMatch(pendingId, itemId))) {
      return;
    }

    commitItems(
      localItems.map((item) => {
        if (!idsMatch(item.id, itemId)) {
          return item;
        }

        return {
          ...item,
          extraFieldValues: {
            ...item.extraFieldValues,
            [fieldKey]: value,
          },
        };
      }),
      { reason: 'extra-field' },
    );
  };

  const handleToggleHidden = (itemId: string | number) => {
    if (disabled) {
      return;
    }

    if (pendingDeleteIds.some((pendingId) => idsMatch(pendingId, itemId))) {
      return;
    }

    const nextItems = localItems.map((item) => {
      if (!idsMatch(item.id, itemId)) {
        return item;
      }

      return {
        ...item,
        isHidden: !item.isHidden,
      };
    });
    const visibleItems = nextItems.filter((item) => !item.isHidden);
    const hiddenItems = nextItems.filter((item) => item.isHidden);
    commitItems(reindexItems([...visibleItems, ...hiddenItems]), { reason: 'hidden-toggle' });
  };

  const handleDelete = async (itemId: string | number) => {
    if (disabled) {
      return;
    }

    if (deleteActionMode === 'mark' && onToggleMarkedForDeletion) {
      replaceLocalItems(
        localItemsRef.current.map((item) => {
          if (!idsMatch(item.id, itemId)) {
            return item;
          }

          return {
            ...item,
            isMarkedForDeletion: !item.isMarkedForDeletion,
          };
        }),
      );
      onToggleMarkedForDeletion(itemId);
      return;
    }

    const timerKey = String(itemId);
    const existingTimer = pendingDeleteTimersRef.current[timerKey];

    if (existingTimer) {
      window.clearTimeout(existingTimer);
      delete pendingDeleteTimersRef.current[timerKey];
      delete pendingDeleteSnapshotsRef.current[timerKey];

      const nextPendingDeleteIds = pendingDeleteIdsRef.current.filter((pendingId) => !idsMatch(pendingId, itemId));
      setPendingDeleteIds(nextPendingDeleteIds);
      publishItems(localItems, nextPendingDeleteIds, { reason: 'delete-undo' });
      return;
    }

    const itemToDelete = localItems.find((item) => idsMatch(item.id, itemId));

    if (!itemToDelete) {
      return;
    }

    pendingDeleteSnapshotsRef.current[timerKey] = {
      item: itemToDelete,
      index: localItems.findIndex((item) => idsMatch(item.id, itemId)),
    };

    const nextPendingDeleteIds = [...pendingDeleteIdsRef.current, itemId];
    setPendingDeleteIds(nextPendingDeleteIds);
    publishItems(localItems, nextPendingDeleteIds, { reason: 'delete' });

    pendingDeleteTimersRef.current[timerKey] = window.setTimeout(async () => {
      delete pendingDeleteTimersRef.current[timerKey];
      delete pendingDeleteSnapshotsRef.current[timerKey];

      const nextPendingDeleteIds = pendingDeleteIdsRef.current.filter((pendingId) => !idsMatch(pendingId, itemId));
      setPendingDeleteIds(nextPendingDeleteIds);
      await Promise.resolve(onDelete?.(itemId));

      const nextItems = reindexItems(localItemsRef.current.filter((item) => !idsMatch(item.id, itemId)));
      replaceLocalItems(nextItems);
      publishItems(nextItems, nextPendingDeleteIds, { reason: 'delete' });
    }, deleteUndoMs);
  };

  const handleSetHero = (itemId: string | number) => {
    if (disabled) {
      return;
    }

    if (pendingDeleteIds.some((pendingId) => idsMatch(pendingId, itemId))) {
      return;
    }

    replaceLocalItems(
      localItemsRef.current.map((item) => ({
        ...item,
        isHero: idsMatch(item.id, itemId),
      })),
    );
    onSetHero?.(itemId);
  };

  const createPendingItem = async (file: File): Promise<PendingImageItem> => {
    const normalizedFile = await normalizeImageFileForUpload({
      file,
      maxBytes: MAX_SVC_IMAGE_UPLOAD_BYTES,
    });
    const dimensions = await getImageDimensions(normalizedFile);
    const fixedAspectRatio = getDefaultPendingCropAspectRatio(dimensions, pendingUploadAspectRatio);
    const croppedAreaPixels = getCenteredCropArea(dimensions, fixedAspectRatio);

    return {
      localId: uuid(),
      file: normalizedFile,
      filename: normalizedFile.name,
      objectUrl: URL.createObjectURL(normalizedFile),
      description: '',
      zoom: 1,
      cropMode: 'fixed',
      fixedAspectRatio,
      croppedAreaPixels,
      dimensions,
      adjustments: { ...DEFAULT_PENDING_IMAGE_ADJUSTMENTS },
      status: 'draft',
    };
  };

  const handleSelectFiles = async (files: Array<File>) => {
    if (!files.length || uploadDisabled || disabled || !onUploadFile) {
      return;
    }

    setIsPreparingUploads(true);

    try {
      if (enablePendingCropUpload) {
        const nextPendingItems = await Promise.all(files.map(createPendingItem));
        updatePendingItems((previousItems) => [...previousItems, ...nextPendingItems]);
        return;
      }

      const uploadResults = await Promise.allSettled(
        files.map(async (file) => {
          const normalizedFile = await normalizeImageFileForUpload({
            file,
            maxBytes: MAX_SVC_IMAGE_UPLOAD_BYTES,
          });
          const newItem = await onUploadFile({
            description: '',
            file: normalizedFile,
            filename: normalizedFile.name,
          });

          if (!newItem) {
            throw new Error('Upload callback did not return an image item');
          }

          return newItem;
        }),
      );

      const firstRejectedUpload = uploadResults.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );

      uploadResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          appendUploadedItem(result.value);
        }
      });

      if (firstRejectedUpload) {
        throw firstRejectedUpload.reason;
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Failed to prepare image upload'), { variant: 'error' });
      reportError(error);
    } finally {
      setIsPreparingUploads(false);
    }
  };

  const handlePendingDescriptionChange = (localId: string, value: string) => {
    if (disabled) {
      return;
    }

    updatePendingItemById(localId, (item) => ({
      ...item,
      description: value,
    }));
  };

  const handlePendingCropChange = (
    localId: string,
    nextCropArea: PendingImageItem['croppedAreaPixels'],
    nextZoom: number,
  ) => {
    if (disabled) {
      return;
    }

    updatePendingItemById(localId, (item) => ({
      ...item,
      croppedAreaPixels: nextCropArea,
      zoom: nextZoom,
    }));
  };

  const handlePendingAdjustmentsChange = (localId: string, nextAdjustments: PendingImageAdjustments) => {
    if (disabled) {
      return;
    }

    updatePendingItemById(localId, (item) => ({
      ...item,
      adjustments: nextAdjustments,
    }));
  };

  const handlePendingCropModeChange = (localId: string, nextCropMode: PendingImageItem['cropMode']) => {
    if (disabled) {
      return;
    }

    updatePendingItemById(localId, (item) => {
      if (item.cropMode === nextCropMode) {
        return item;
      }

      if (nextCropMode === 'freeform') {
        return {
          ...item,
          cropMode: nextCropMode,
        };
      }

      return {
        ...item,
        cropMode: nextCropMode,
        croppedAreaPixels: getCropAreaForAspectRatio({
          aspectRatio: item.fixedAspectRatio,
          cropArea: item.croppedAreaPixels,
          dimensions: item.dimensions,
        }),
      };
    });
  };

  const handlePendingReset = (localId: string) => {
    if (disabled) {
      return;
    }

    updatePendingItemById(localId, (item) => ({
      ...item,
      adjustments: { ...DEFAULT_PENDING_IMAGE_ADJUSTMENTS },
      cropMode: 'fixed',
      croppedAreaPixels: getCenteredCropArea(item.dimensions, item.fixedAspectRatio),
      zoom: 1,
    }));
  };

  const handlePendingUpload = async (localId: string) => {
    if (disabled) {
      return;
    }

    const pendingItem = pendingItemsRef.current.find((item) => item.localId === localId);

    if (!pendingItem || pendingItem.status === 'uploading') {
      return;
    }

    await uploadPendingItem(pendingItem);
  };

  useImperativeHandle(
    ref,
    () => ({
      addImageToList(newImages: Array<FlatImageItem>) {
        const nextItems = reindexItems([...localItemsRef.current, ...newImages]);
        replaceLocalItems(nextItems);
        publishItems(nextItems);
      },
      async uploadAllPending(): Promise<FlatImageEditorUploadAllPendingResult> {
        const pendingItemsToUpload = pendingItemsRef.current.filter((item) => item.status !== 'uploading');

        if (!pendingItemsToUpload.length) {
          return {
            uploadedCount: 0,
            failedCount: 0,
            failedItems: [],
          };
        }

        const baseOrder = localItemsRef.current.length;
        const uploadResults = await Promise.all(
          pendingItemsToUpload.map((pendingItem, index) => uploadPendingItem(pendingItem, baseOrder + index + 1)),
        );

        const nextItems = reindexItems([...localItemsRef.current].sort((left, right) => left.order - right.order));
        replaceLocalItems(nextItems);
        publishItems(nextItems, pendingDeleteIdsRef.current);

        const failedItems = uploadResults.filter(
          (result): result is Extract<PendingUploadAttemptResult, { status: 'rejected' }> =>
            result.status === 'rejected',
        );

        if (failedItems.length) {
          enqueueSnackbar(
            `${failedItems.length} image upload${failedItems.length === 1 ? '' : 's'} failed. Fix them and try again.`,
            { variant: 'error' },
          );
        }

        return {
          uploadedCount: uploadResults.length - failedItems.length,
          failedCount: failedItems.length,
          failedItems,
        };
      },
    }),
    [enqueueSnackbar, publishItems, replaceLocalItems, uploadPendingItem],
  );

  const renderedItems = useMemo(
    () =>
      localItems.map((item) => ({
        ...item,
        isPendingDelete: pendingDeleteIds.some((pendingId) => idsMatch(pendingId, item.id)),
      })),
    [localItems, pendingDeleteIds],
  );

  const itemIds = useMemo(() => renderedItems.map((item) => item.id), [renderedItems]);
  const shouldShowUploadTile = !!onUploadFile;

  return (
    <Box mt={1}>
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (disabled || !showReorderAction || !over || active.id === over.id) {
            return;
          }

          const oldIndex = localItems.findIndex((item) => item.id === active.id);
          const newIndex = localItems.findIndex((item) => item.id === over.id);

          if (oldIndex === -1 || newIndex === -1 || localItems[oldIndex]?.canReorder === false) {
            return;
          }

          commitItems(reindexItems(arrayMove(localItems, oldIndex, newIndex)), { reason: 'reorder' });
        }}
        sensors={sensors}
      >
        <SortableContext items={itemIds} strategy={rectSortingStrategy}>
          <Box
            sx={{
              alignItems: 'start',
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
            }}
          >
            {renderedItems.map((item) => (
              <FlatImageTile
                key={item.id}
                customActions={renderItemActions?.(item)}
                deleteActionLabel={deleteActionLabel}
                disabled={disabled}
                descriptionLabel={descriptionLabel}
                extraFields={extraFields}
                item={item}
                restoreActionLabel={restoreActionLabel}
                showDeleteAction={showDeleteAction}
                showDescriptionField={showDescriptionField}
                showHeroAction={showHeroAction}
                showHiddenToggle={showHiddenToggle}
                showReorderAction={showReorderAction}
                onDelete={handleDelete}
                onDescriptionChange={handleDescriptionChange}
                onExtraFieldChange={handleExtraFieldChange}
                onSetHero={showHeroAction ? handleSetHero : undefined}
                onToggleHidden={showHiddenToggle ? handleToggleHidden : undefined}
              />
            ))}

            {enablePendingCropUpload
              ? pendingItems.map((item) => (
                  <PendingImageTile
                    key={item.localId}
                    descriptionLabel={descriptionLabel}
                    disabled={disabled}
                    item={item}
                    showDescriptionField={showDescriptionField}
                    onAdjustmentsChange={handlePendingAdjustmentsChange}
                    onCropChange={handlePendingCropChange}
                    onCropModeChange={handlePendingCropModeChange}
                    onDescriptionChange={handlePendingDescriptionChange}
                    onRemove={(localId) =>
                      updatePendingItems((previousItems) =>
                        previousItems.filter((pendingItem) => pendingItem.localId !== localId),
                      )
                    }
                    onReset={handlePendingReset}
                    onUpload={handlePendingUpload}
                  />
                ))
              : null}

            {shouldShowUploadTile ? (
              <ImageUploadTile
                disabled={uploadDisabled || isPreparingUploads || disabled}
                helperText={uploadHelperText}
                isBusy={isPreparingUploads}
                label={uploadLabel}
                onSelectFiles={handleSelectFiles}
              />
            ) : null}
          </Box>
        </SortableContext>
      </DndContext>

      {renderedItems.length === 0 && pendingItems.length === 0 && !shouldShowUploadTile ? (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {emptyStateMessage}
        </Typography>
      ) : null}
    </Box>
  );
});

export default FlatImageEditor;
